import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, BarChart3, Star, Pencil, Loader2 } from "lucide-react";
import { api } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CostoEuro from "./CostoEuro";
import LetturaOperativa from "./LetturaOperativa";
import ContoDisservizi from "./ContoDisservizi";
import HotelOggi from "./HotelOggi";
import NumeriVeri from "./NumeriVeri";
import Notifiche from "./Notifiche";

const COV = { FULL: "Quadro completo", PARTIAL: "Quadro parziale", BLOCKED_TECHNICALLY: "Analisi da completare" };

const DEFAULT_TITLE = "Il mio hotel, oggi: prima le persone e le camere, poi tutto il resto.";

function HeroTitle({ scope }) {
  const [t, setT] = useState(null);
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.heroTitle(scope.tenant, scope.hotel).then((r) => { setT(r.title || ""); setVal(r.title || ""); }).catch(() => setT("")); }, [scope.tenant, scope.hotel]);
  const save = async (e) => { e.preventDefault(); setBusy(true); try { const r = await api.heroTitleSet({ tenant: scope.tenant, hotel: scope.hotel, title: val }); setT(r.title); setEdit(false); toast.success(r.title ? "Frase d'apertura salvata." : "Torna la frase predefinita di LEUTELIA."); } catch (err) { toast.error(err.message); } finally { setBusy(false); } };
  if (t === null) return null;
  if (edit) return (
    <form onSubmit={save} className="flex flex-wrap items-center gap-2 w-full" data-testid="hero-title-form">
      <Input value={val} onChange={(e) => setVal(e.target.value)} maxLength={140} placeholder={DEFAULT_TITLE} className="h-10 flex-1 min-w-[260px] bg-white/95 text-[14px]" data-testid="hero-title-input" autoFocus />
      <Button type="submit" disabled={busy} className="h-10 bg-brand-gold text-brand-ink text-[12px] font-bold" data-testid="hero-title-save">{busy ? <Loader2 size={13} className="animate-spin" /> : "Salva"}</Button>
      <Button type="button" variant="outline" onClick={() => { setEdit(false); setVal(t); }} className="h-10 border-brand-cream/40 bg-transparent text-brand-cream hover:bg-brand-cream/10 hover:text-brand-cream text-[12px]" data-testid="hero-title-cancel">Annulla</Button>
    </form>);
  return (
    <span className="flex items-center gap-2 flex-wrap">
      <span className="font-serif italic text-[16px] text-brand-cream/95" data-testid="hero-title-text">{t || DEFAULT_TITLE}</span>
      <button type="button" onClick={() => setEdit(true)} className="text-brand-gold hover:text-brand-cream" title="Scrivi la tua frase d'apertura" data-testid="hero-title-edit"><Pencil size={13} /></button>
    </span>);
}

function HeroHotel({ ccu, scope }) {
  const id = ccu.canonical_identity || {};
  const hero = id.hero_image;
  const stars = (() => { const m = String(id.category || "").match(/(\d)/); return m ? Number(m[1]) : 0; })();
  const city = id.locality || "";
  const today = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  return (
    <section className="relative rounded-2xl overflow-hidden mb-6 min-h-[260px] sm:min-h-[320px] bg-brand-greenDark" data-testid="hero-hotel">
      {hero && <img src={hero} alt={id.name || ""} className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-t from-brand-greenDark via-brand-greenDark/55 to-brand-greenDark/10" />
      <div className="relative h-full min-h-[260px] sm:min-h-[320px] p-6 sm:p-9 flex flex-col justify-end">
        <div className="text-[10.5px] font-bold tracking-[0.24em] text-brand-gold uppercase">— {today}</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-brand-cream mt-2" data-testid="hero-hotel-name">{id.name || scope.hotel}</h1>
        <div className="flex flex-wrap items-center gap-3 mt-3 text-brand-cream/90 text-[13px]">
          {stars > 0 && <span className="flex items-center gap-0.5 text-brand-gold" data-testid="hero-hotel-stars">{Array.from({ length: Math.min(5, Math.round(stars)) }).map((_, i) => <Star key={i} size={13} fill="currentColor" />)}</span>}
          {city && <span>{city}</span>}
          <HeroTitle scope={scope} />
        </div>
      </div>
    </section>
  );
}

function VaiAllAnalisi({ ccu, onNav }) {
  const cs = ccu.cognitive_state;
  const d = ccu.delta_vs_previous;
  const nuovi = d.new_criticalities.length;
  const when = ccu.computed_at ? new Date(ccu.computed_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) : "—";
  return (
    <button type="button" onClick={() => onNav?.("analisi")} className="w-full text-left bg-white rounded-xl border border-brand-gold/25 border-l-4 border-l-brand-gold p-5 mb-8 flex flex-wrap items-center gap-4 hover:border-brand-gold transition-colors" data-testid="vai-analisi">
      <span className="w-11 h-11 rounded-full bg-brand-green text-brand-gold flex items-center justify-center shrink-0"><BarChart3 size={18} /></span>
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] font-bold tracking-[0.2em] text-brand-muted uppercase">Analisi dell'hotel · versione {ccu.understanding_version} · {COV[cs.coverage_status.level] || COV.BLOCKED_TECHNICALLY} · {when}</div>
        <div className="font-serif text-[20px] text-brand-ink mt-0.5">{nuovi ? `${nuovi} nuov${nuovi === 1 ? "a criticità" : "e criticità"} e ${cs.open_contradictions.length} contraddizioni aperte: apri la lettura completa.` : "Sito, Google, recensioni, OTA, social e posizionamento: la lettura completa di LEUTELIA."}</div>
      </div>
      <span className="text-[12px] font-bold text-brand-green flex items-center gap-1">Apri l'analisi <ArrowRight size={14} /></span>
    </button>
  );
}

export default function Panoramica({ ccu, scope, onNav }) {
  return (
    <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-5 lg:py-7 max-w-[1320px] 2xl:max-w-[1560px] min-[2000px]:max-w-[1760px] leu-in" data-testid="panoramica-operativa">
      <HeroHotel ccu={ccu} scope={scope} />
      <NumeriVeri scope={scope} />
      <HotelOggi scope={scope} onNav={onNav} />
      <Notifiche />
      <div className="mb-8"><ContoDisservizi scope={scope} compact onNav={onNav} /></div>
      <CostoEuro scope={scope} />
      <LetturaOperativa scope={scope} onNav={onNav} />
      <VaiAllAnalisi ccu={ccu} onNav={onNav} />
    </div>
  );
}
